// @ts-nocheck
import React, { useMemo, useState, useEffect } from "react";
import SharedSelect from "../components/SharedSelect";
import "../styles/Home.css";
import { useNavigate } from "react-router-dom";
import { fetchListings } from "../api/listings";
import { getUser } from "../auth/auth";
import { getBrowsingLocation, getBrowsingRadius, hasCoordinates, haversineMiles } from "../utils/location";

const CHIPS = ["All", "Pokémon", "One Piece", "Yu-Gi-Oh!", "MTG", "Sports", "Other"];

const CONDITIONS = ["All", "NM", "LP", "MP", "HP", "DMG", "Sealed", "Slab 10"];
const LISTING_TYPES = ["All", "Sale", "Trade", "Both"];
const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low → High" },
  { value: "price_desc", label: "Price: High → Low" },
];

export default function Marketplace() {
  const navigate = useNavigate();
  const user = getUser();
  const browsingLocation = getBrowsingLocation(user);
  const browsingRadiusMiles = getBrowsingRadius(user);
  const [query, setQuery] = useState("");
  const [chip, setChip] = useState("All");
  const [condition, setCondition] = useState("All");
  const [listingType, setListingType] = useState("All");
  const [sort, setSort] = useState("newest");
  const [nearBrowsingOnly, setNearBrowsingOnly] = useState(false);

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    fetchListings()
      .then((data) => {
        if (active) setListings(data || []);
      })
      .catch(() => {
        if (active) setError("Failed to load listings");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    let items = listings.filter((x) => {
      const matchesChip = chip === "All" || x.game === chip;
      const matchesCondition = condition === "All" || x.condition === condition;
      const matchesType = listingType === "All" || x.listingType?.toLowerCase() === listingType.toLowerCase();

      const matchesQuery =
        !q ||
        x.title.toLowerCase().includes(q) ||
        x.seller.toLowerCase().includes(q) ||
        x.game.toLowerCase().includes(q);

      return matchesChip && matchesCondition && matchesType && matchesQuery;
    });

    if (sort === "price_asc") items = [...items].sort((a, b) => a.price - b.price);
    if (sort === "price_desc") items = [...items].sort((a, b) => b.price - a.price);

    if (nearBrowsingOnly && hasCoordinates(browsingLocation)) {
      items = items
        .map((item) => {
          if (!hasCoordinates(item.location)) return null;
          const distanceMiles = Math.round(
            haversineMiles(
              browsingLocation.latitude,
              browsingLocation.longitude,
              item.location.latitude,
              item.location.longitude,
            ),
          );
          return distanceMiles <= browsingRadiusMiles ? { ...item, distanceMiles } : null;
        })
        .filter(Boolean);
    }

    return items;
  }, [browsingLocation, browsingRadiusMiles, chip, condition, listingType, listings, nearBrowsingOnly, query, sort]);

  return (
    <div className="home" style={{ 
      background: 'radial-gradient(1200px 800px at 80% 10%, rgba(88, 101, 242, 0.18), transparent 55%), radial-gradient(900px 700px at 10% 20%, rgba(0, 209, 255, 0.18), transparent 55%), #060914',
      minHeight: '100vh',
      padding: 28,
      margin: 0,
      maxWidth: '100%'
    }}>
      <header className="home-top">
        <div className="home-title">
          <h2>Marketplace</h2>
          <p>Buy, sell, and trade collectibles on Cardex.</p>
        </div>

        <div className="home-actions">
          <button className="btn-action" type="button" onClick={() => navigate("/create")}>
            + Create Listing
          </button>
          <button className="btn-action secondary" type="button" onClick={() => navigate("/home")}>
            Dashboard
          </button>
        </div>
      </header>

      <section className="controls">
        <input
          className="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search cards, sellers, or games…"
        />

        <div className="filters">
          <SharedSelect value={chip} onChange={(e) => setChip(e.target.value)}>
            {CHIPS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </SharedSelect>

          <SharedSelect value={condition} onChange={(e) => setCondition(e.target.value)}>
            {CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </SharedSelect>

          <SharedSelect value={listingType} onChange={(e) => setListingType(e.target.value)}>
            {LISTING_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </SharedSelect>

          <SharedSelect value={sort} onChange={(e) => setSort(e.target.value)}>
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </SharedSelect>
          {hasCoordinates(browsingLocation) ? (
            <label className="filter-check-control" style={{ alignSelf: "center" }}>
              <input type="checkbox" checked={nearBrowsingOnly} onChange={() => setNearBrowsingOnly((prev) => !prev)} />
              <span>Near browsing area</span>
            </label>
          ) : null}
        </div>
      </section>

      {loading && (
        <div className="empty">
          <h3>Loading listings…</h3>
          <p>Please wait a moment.</p>
        </div>
      )}

      {error && (
        <div className="empty">
          <h3>Couldn’t load listings</h3>
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <section className="grid">
          {filtered.map((item) => (
          <article
            key={item.id}
            className="listing"
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/listing/${item.id}`)}
            onKeyDown={(e) => e.key === "Enter" && navigate(`/listing/${item.id}`)}
          >
            <div className="thumb">
              <img src={item.image} alt={item.title} loading="lazy" />
              <span className="badge">{item.game}</span>
              {item.status === "sold" && <span className="sold-badge">Sold</span>}
            </div>

            <div className="listing-body">
              <div className="row">
                <h3 className="listing-title">{item.title}</h3>
                <div className="price">${Number(item.price).toFixed(2)}</div>
              </div>

              <div className="meta">
                <span className="pill">{item.condition}</span>
                <span className="dot">•</span>
                <span className="seller">@{item.seller}</span>
                <span className="dot">•</span>
                <span className="time">{item.time}</span>
              </div>
              <div className="meta">
                <span>{item.location?.label || item.locationPostalCode || "Location unavailable"}</span>
                {item.distanceMiles != null ? (
                  <>
                    <span className="dot">•</span>
                    <span>{item.distanceMiles} mi away</span>
                  </>
                ) : null}
              </div>
            </div>
          </article>
          ))}
        </section>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="empty">
          <h3>No results</h3>
          <p>Try a different search or clear your filters.</p>
        </div>
      )}
    </div>
  );
}
